import { useState } from 'react';
import { Copy, Lock, Unlock } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Textarea, Input, Field } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';

/** Copy a Uint8Array into a fresh ArrayBuffer — needed for crypto.subtle on stricter TS libs. */
function buf(u: Uint8Array): ArrayBuffer {
  const b = new ArrayBuffer(u.byteLength);
  new Uint8Array(b).set(u);
  return b;
}

async function deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    buf(new TextEncoder().encode(password)),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: buf(salt), iterations: 250_000, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

function b64encode(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}
function b64decode(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function encrypt(plaintext: string, password: string): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(password, salt);
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: buf(iv) },
    key,
    buf(new TextEncoder().encode(plaintext)),
  );
  const out = new Uint8Array(salt.length + iv.length + ct.byteLength);
  out.set(salt, 0);
  out.set(iv, salt.length);
  out.set(new Uint8Array(ct), salt.length + iv.length);
  return b64encode(out);
}

async function decrypt(ciphertext: string, password: string): Promise<string> {
  const all = b64decode(ciphertext);
  const salt = all.slice(0, 16);
  const iv = all.slice(16, 28);
  const ct = all.slice(28);
  const key = await deriveKey(password, salt);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: buf(iv) }, key, buf(ct));
  return new TextDecoder().decode(pt);
}

export function AesEncrypt() {
  return (
    <div>
      <ToolHeader
        toolId="aes-encrypt"
        title="AES-256 encrypt / decrypt"
        description="Authenticated encryption with PBKDF2-derived keys (250k iterations, SHA-256)."
      />
      <Tabs defaultValue="enc">
        <TabsList className="mb-4">
          <TabsTrigger value="enc"><Lock size={11} className="mr-1" /> Encrypt</TabsTrigger>
          <TabsTrigger value="dec"><Unlock size={11} className="mr-1" /> Decrypt</TabsTrigger>
        </TabsList>
        <TabsContent value="enc"><EncryptPanel /></TabsContent>
        <TabsContent value="dec"><DecryptPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function EncryptPanel() {
  const [text, setText] = useState('');
  const [pw, setPw] = useState('');
  const [out, setOut] = useState('');

  return (
    <div className="space-y-3">
      <Card>
        <Field label="Passphrase" className="mb-3">
          <Input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Strong passphrase…"
          />
        </Field>
        <Field label="Plaintext">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="min-h-[140px]"
            placeholder="Type or paste text to encrypt…"
          />
        </Field>
        <Button
          variant="primary"
          className="mt-3"
          disabled={!text || !pw}
          onClick={async () => {
            try {
              const c = await encrypt(text, pw);
              setOut(c);
              toast.success('Encrypted');
            } catch {
              toast.error('Encryption failed');
            }
          }}
        >
          <Lock size={11} />
          Encrypt
        </Button>
      </Card>
      {out && (
        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <span className="text-xs font-medium text-fg-muted">Ciphertext (Base64)</span>
            <Button size="sm" variant="ghost" onClick={async () => {
              const ok = await copyToClipboard(out);
              if (ok) toast.success('Copied');
            }}>
              <Copy size={11} />
            </Button>
          </div>
          <pre className="font-mono text-[12px] leading-relaxed p-4 break-all whitespace-pre-wrap">{out}</pre>
        </Card>
      )}
    </div>
  );
}

function DecryptPanel() {
  const [cipher, setCipher] = useState('');
  const [pw, setPw] = useState('');
  const [out, setOut] = useState('');
  const [err, setErr] = useState('');

  return (
    <div className="space-y-3">
      <Card>
        <Field label="Passphrase" className="mb-3">
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} />
        </Field>
        <Field label="Ciphertext (Base64)">
          <Textarea
            mono
            value={cipher}
            onChange={(e) => setCipher(e.target.value)}
            className="min-h-[140px]"
          />
        </Field>
        <Button
          variant="primary"
          className="mt-3"
          disabled={!cipher || !pw}
          onClick={async () => {
            setErr('');
            try {
              const t = await decrypt(cipher, pw);
              setOut(t);
              toast.success('Decrypted');
            } catch (e) {
              setErr(e instanceof Error ? e.message : 'Decryption failed');
              setOut('');
            }
          }}
        >
          <Unlock size={11} />
          Decrypt
        </Button>
      </Card>
      {err && <Card className="text-danger">⚠ {err}</Card>}
      {out && (
        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <span className="text-xs font-medium text-fg-muted">Plaintext</span>
            <Button size="sm" variant="ghost" onClick={async () => {
              const ok = await copyToClipboard(out);
              if (ok) toast.success('Copied');
            }}>
              <Copy size={11} />
            </Button>
          </div>
          <pre className="font-mono text-[13px] leading-relaxed p-4 break-all whitespace-pre-wrap">{out}</pre>
        </Card>
      )}
    </div>
  );
}

export default AesEncrypt;
